import {useState} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

import {vidyoProviderLogs} from '@/utils/general';

import {PermissionState, useMediaPermissions} from './useMediaPermissions';

import styles from './videoCall.module.scss';

type Props = {
    cameraPermission: PermissionState;
    micPermission: PermissionState;
};

const isBlocked = (state: PermissionState) => state === 'denied' || state === 'prompt';

export const PermissionsBanner = ({cameraPermission, micPermission}: Props) => {
    const {requestPermissions} = useMediaPermissions();
    const [showHelp, setShowHelp] = useState(false);

    const cameraBlocked = isBlocked(cameraPermission);
    const micBlocked = isBlocked(micPermission);
    const canRequestCamera = cameraBlocked && cameraPermission === 'prompt';
    const canRequestMic = micBlocked && micPermission === 'prompt';
    const canRequest = canRequestCamera || canRequestMic;

    if (!cameraBlocked && !micBlocked) {
        vidyoProviderLogs({
            message: 'Permission for camera and microphone granted',
            eventType: 'device',
            status: 'success',
        });

        return null;
    }

    vidyoProviderLogs({
        message:
            'PermissionsBanner rendered with blocked permissions' +
            `(camera:      ${cameraPermission}, mic: ${micPermission})`,
        eventType: 'device',
        status: 'error',
    });

    const blockedList = [cameraBlocked && 'Camera', micBlocked && 'Microphone'].filter(Boolean).join(' & ');

    const handleRequestAccess = async () => {
        await requestPermissions({
            video: canRequestCamera,
            audio: canRequestMic,
        });
    };

    return (
        <>
            <div className={styles.permissionsBanner} role="alert">
                <div className={styles.permissionsBannerIcons}>
                    {cameraBlocked && <VideocamOffIcon className={styles.permissionsBannerIcon} />}
                    {micBlocked && <MicOffIcon className={styles.permissionsBannerIcon} />}
                </div>

                <div className={styles.permissionsBannerBody}>
                    <strong className={styles.permissionsBannerTitle}>{blockedList} access is blocked</strong>
                    <span className={styles.permissionsBannerHint}>
                        {canRequest ? (
                            'Click "Request Access" below to grant permissions, or manually allow access in the browser address bar. The banner will close automatically.'
                        ) : (
                            <>
                                Click the&nbsp;<strong>lock</strong>&nbsp;or&nbsp;<strong>camera</strong>&nbsp;icon in
                                the Chrome address bar and allow access. The banner will close automatically.
                            </>
                        )}
                    </span>
                </div>

                <div className={styles.permissionsBannerActions}>
                    {canRequest && (
                        <button
                            className={styles.permissionsBannerBtn}
                            title="Request camera & microphone access"
                            onClick={() => {
                                void handleRequestAccess();
                            }}
                        >
                            <RefreshIcon fontSize="small" />
                            Request Access
                        </button>
                    )}
                    <button
                        className={styles.permissionsBannerBtn}
                        title="How to enable camera & microphone"
                        onClick={() => setShowHelp(true)}
                    >
                        <HelpOutlineIcon fontSize="small" />
                        How to grant permissions
                    </button>
                </div>
            </div>

            {showHelp && (
                <div
                    className={styles.permissionsHelpOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label="How to enable permissions"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowHelp(false);
                    }}
                >
                    <div className={styles.permissionsHelpPopup}>
                        <div className={styles.permissionsHelpHeader}>
                            <span className={styles.permissionsHelpTitle}>How to allow {blockedList}</span>
                            <button
                                className={styles.permissionsHelpClose}
                                aria-label="Close"
                                onClick={() => setShowHelp(false)}
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        </div>

                        <ol className={styles.permissionsHelpSteps}>
                            <li>
                                Look at the <strong>address bar</strong> at the top of your browser.
                            </li>
                            <li>
                                Click the&nbsp;
                                <span className={styles.permissionsHelpInlineIcon}>
                                    <InfoIcon style={{fontSize: 14, verticalAlign: 'middle'}} />
                                </span>
                                &nbsp;<strong>info</strong> icon or the&nbsp;
                                <span className={styles.permissionsHelpInlineIcon}>
                                    <VideocamIcon style={{fontSize: 14, verticalAlign: 'middle'}} />
                                </span>
                                &nbsp;/&nbsp;
                                <span className={styles.permissionsHelpInlineIcon}>
                                    <MicIcon style={{fontSize: 14, verticalAlign: 'middle'}} />
                                </span>
                                &nbsp;icon on the left side of the URL.
                            </li>
                            <li>
                                In the dropdown, find <strong>{blockedList}</strong> and change it from&nbsp;
                                <span className={styles.permissionsHelpInlineIcon}>
                                    <ToggleOffIcon style={{fontSize: 18, verticalAlign: 'middle', color: '#999'}} />
                                </span>
                                &nbsp;<em>Blocked</em>&nbsp;to&nbsp;
                                <span className={styles.permissionsHelpInlineIcon}>
                                    <ToggleOnIcon style={{fontSize: 18, verticalAlign: 'middle', color: '#2e7d32'}} />
                                </span>
                                &nbsp;<strong>Allow</strong>.
                            </li>
                            <li>
                                Click <strong>Reload</strong> when prompted, or use the button below.
                            </li>
                        </ol>

                        <div className={styles.permissionsHelpFooter}>
                            <button
                                className={styles.permissionsHelpReloadBtn}
                                onClick={() => window.location.reload()}
                            >
                                <RefreshIcon fontSize="small" />
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
